"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveDateFromExpression = exports.resolveRelativeDateTimeFromExpression = void 0;
const date_fns_1 = require("date-fns");
/**
 * The supported keys come from the backend implementation
 * https://stash.mnemonic.no/projects/SRV/repos/argus-common/browse/spi/src/main/java/no/mnemonic/services/argus/common/spi/json/TimeFieldUtils.java#102
 */
const TEMPORAL_FUNCTIONS = {
    s: date_fns_1.addSeconds,
    sec: date_fns_1.addSeconds,
    seconds: date_fns_1.addSeconds,
    m: date_fns_1.addMinutes,
    min: date_fns_1.addMinutes,
    minute: date_fns_1.addMinutes,
    minutes: date_fns_1.addMinutes,
    h: date_fns_1.addHours,
    hr: date_fns_1.addHours,
    hour: date_fns_1.addHours,
    hours: date_fns_1.addHours,
    d: date_fns_1.addDays,
    day: date_fns_1.addDays,
    days: date_fns_1.addDays,
    w: date_fns_1.addWeeks,
    week: date_fns_1.addWeeks,
    weeks: date_fns_1.addWeeks,
    month: date_fns_1.addMonths,
    months: date_fns_1.addMonths,
    quarter: date_fns_1.addQuarters,
    quarters: date_fns_1.addQuarters,
    y: date_fns_1.addYears,
    yr: date_fns_1.addYears,
    year: date_fns_1.addYears,
    years: date_fns_1.addYears,
};
/**
 * The supported keys come from the backend implementation
 * https://stash.mnemonic.no/projects/SRV/repos/argus-common/browse/spi/src/main/java/no/mnemonic/services/argus/common/spi/json/TimeFieldUtils.java#75
 */
const INITIAL_FUNCTIONS = {
    now: (val) => new Date(val),
    startofhour: date_fns_1.startOfHour,
    startofday: date_fns_1.startOfDay,
    startofweek: date_fns_1.startOfWeek,
    startofmonth: date_fns_1.startOfMonth,
    startofquarter: date_fns_1.startOfQuarter,
    startofyear: date_fns_1.startOfYear,
    endofhour: date_fns_1.endOfHour,
    endofday: date_fns_1.endOfDay,
    endofweek: date_fns_1.endOfWeek,
    endofmonth: date_fns_1.endOfMonth,
    endofquarter: date_fns_1.endOfQuarter,
    endofyear: date_fns_1.endOfYear,
};
/**
 * Resolves a relative date-time expression into structured components.
 *
 * @param {string} input - The input string representing a relative date-time expression (e.g., "now + 2 days", "startOfMonth - 1 week").
 * @returns {TResolvedRelativeDateTimeExpression | TResolvedRelativeDateTimeExpression[] | null}
 * - A structured object (or an array of objects) representing the resolved expression(s), or `null` if the input is invalid or cannot be parsed.
 *
 */
const resolveRelativeDateTimeFromExpression = (input) => {
    if (!input || typeof input !== "string")
        return null;
    const parts = [];
    let initialFunctionFound = false;
    // Extract and remove any base keywords from the input
    const initialFunctionMatch = input
        ?.toLowerCase()
        ?.match(new RegExp(`\\b(${Object.keys(INITIAL_FUNCTIONS).join("|")})\\b`));
    if (initialFunctionMatch) {
        initialFunctionFound = true;
        input = input.replace(initialFunctionMatch[0], "").trim(); // Remove base keyword from input for further processing
    }
    const regex = /([+-]?)\s*(\d+)\s*(\b\w+\b)/g;
    let match;
    while ((match = regex.exec(input)) !== null) {
        const operator = match[1] === "-" ? "-" : "";
        const amount = operator + match[2]; // Include the operator in the amount if it's "-"
        const temporalFunction = match[3];
        if (initialFunctionFound) {
            parts.push({ initialFunction: initialFunctionMatch?.[0], amount, temporalFunction });
            initialFunctionFound = false;
        }
        else {
            parts.push({ amount, temporalFunction });
        }
    }
    if (parts?.length === 0 && initialFunctionFound) {
        return { initialFunction: initialFunctionMatch?.[0] };
    }
    if (parts?.length === 0)
        return null;
    return parts?.length === 1 ? parts[0] : parts;
};
exports.resolveRelativeDateTimeFromExpression = resolveRelativeDateTimeFromExpression;
/**
 * Resolves a date based on a string expression and an optional reference date.
 *
 * This function parses a date expression (relative or absolute) and calculates
 * the corresponding `Date` object.
 *
 * @param {string} expression - The date expression to be resolved.
 * @param {Date} [date] - An optional reference date. If not provided, the current date (`new Date()`) will be used. Mainly for dependency injection in tests.
 *
 * @returns {Date | null}
 * - Returns a `Date` object representing the resolved date.
 * - Returns `null` if the expression is invalid or cannot be resolved.
 *
 * @throws This function handles errors gracefully by returning `null` in case of invalid expressions.
 */
const resolveDateFromExpression = (expression, date) => {
    if (!expression)
        return null;
    if (typeof expression === "number")
        return new Date(expression);
    const now = date ? new Date(date) : new Date();
    try {
        const relative = (0, exports.resolveRelativeDateTimeFromExpression)(expression);
        if (!relative)
            return null;
        if (Array.isArray(relative)) {
            return relative.reduce((currentDate, item) => {
                const date = resolveInitialFunction(item?.initialFunction, currentDate);
                return resolveTemporalFunction(item, date);
            }, now);
        }
        return resolveTemporalFunction(relative, resolveInitialFunction(relative?.initialFunction, now));
    }
    catch (error) {
        return null;
    }
};
exports.resolveDateFromExpression = resolveDateFromExpression;
const resolveTemporalFunction = (relative, date) => {
    if (!relative?.amount || !relative?.temporalFunction)
        return date;
    const { amount, temporalFunction } = relative;
    const operation = TEMPORAL_FUNCTIONS[temporalFunction.toLocaleLowerCase()];
    if (!operation) {
        throw Error(`Unsupported temporalFunction: ${temporalFunction}`);
    }
    return operation(date, Number(amount));
};
const resolveInitialFunction = (initialFunction, date) => {
    if (!initialFunction)
        return date;
    const operation = INITIAL_FUNCTIONS[initialFunction.toLocaleLowerCase()];
    if (!operation) {
        throw new Error(`Unsupported initialFunction: ${initialFunction}`);
    }
    return operation(date);
};
